/**
 * First-party play/pause preference cookie (src/persist, AD-7). Persistence is
 * optional and non-load-bearing: every read is defensive, every write may silently
 * fail, and the full simulator works when cookies are absent, blocked, or cleared.
 * An unreadable preference resolves to PAUSED at the call site.
 *
 * The cookie stores ONLY the play/pause value ('play' | 'pause'). It holds no
 * animation time, no configuration, no personal data, and no tracking identifier
 * (AC 6). It is set same-origin (`Path=/`, `SameSite=Lax`, no cross-site or
 * third-party attributes, AD-6) with a modest lifetime.
 *
 * This is NOT part of `AppState`: the reducer never reads or writes it, so `Reset`
 * leaves it untouched and a later reopen still honours a stored preference (AC 9).
 */

export type PlaybackPref = 'play' | 'pause';

/** Cookie name; same-origin, first-party. */
const COOKIE_NAME = 'kepler_playback';
/** Modest lifetime: one year in seconds. */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/** Guard against a non-browser context, matching src/platform/capabilities.ts. */
function cookieUnavailable(): boolean {
  return typeof document === 'undefined';
}

/**
 * Defensive reader. Returns `'play'`/`'pause'` for a valid cookie, or `null` when
 * the cookie is absent, blocked, unsupported, or malformed. Never throws.
 */
export function readPlaybackPref(): PlaybackPref | null {
  if (cookieUnavailable()) return null;
  try {
    const raw = document.cookie;
    if (typeof raw !== 'string' || raw.length === 0) return null;
    for (const part of raw.split(';')) {
      const eq = part.indexOf('=');
      if (eq === -1) continue;
      const name = part.slice(0, eq).trim();
      if (name !== COOKIE_NAME) continue;
      const value = decodeURIComponent(part.slice(eq + 1).trim());
      if (value === 'play' || value === 'pause') return value;
      // Any other (malformed/unsupported) value reads back as null.
      return null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Defensive writer. Persists ONLY the play/pause value; swallows a throwing or
 * unavailable `document.cookie` (AD-7). Called exclusively from the explicit
 * Play/Pause user-action handlers, never at startup and never on Reset.
 */
export function writePlaybackPref(pref: PlaybackPref): void {
  if (cookieUnavailable()) return;
  try {
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(pref)}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
  } catch {
    // Writes may silently fail; persistence is non-load-bearing.
  }
}
